import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import {Box, Typography, Chip, Link} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import apiService from '../../services/apiService';
import {formatLastSeen, getMachineStateColor} from '../../utils/formatters';
import {useTableUrlState} from '../../hooks/useTableUrlState';

const MachinesPage = () => {
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const {pagination, setPagination, sorting, setSorting} = useTableUrlState({
        initialSorting: [{id: 'name', desc: false}], initialPageSize: 20
    });

    const fetchData = useCallback(() => {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });
        if (sorting.length > 0) {
            const sortParam = sorting.map(s => `${s.id},${s.desc ? 'desc' : 'asc'}`).join(',');
            params.append('sort', sortParam);
        }
        apiService.get(`/api/machines?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
            })
            .catch(err => setError(err.message || 'Failed to fetch machines'))
            .finally(() => setIsLoading(false));
    }, [pagination, sorting]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // The column definitions remain exactly the same
    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 320,
                Cell: ({cell}) => (
                    <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'mac',
                header: 'MAC Address',
                size: 120,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                size: 120,
            },
            {
                id: 'categoryId',
                header: 'Category',
                size: 80,
                Cell: ({cell, row}) => (
                    row.original.category ?
                        <Link component={RouterLink} to={`/categories/${row.original.category.id}`}>
                            {row.original.category.identifier}
                        </Link> : <Typography>-</Typography>
                ),
            },
            {
                accessorKey: 'lastHeartbeatTime',
                header: 'Last Seen',
                size: 180,
                Cell: ({cell}) => formatLastSeen(cell.getValue()),
            },
            {
                accessorKey: 'state',
                header: 'State',
                size: 80,
                Cell: ({cell}) => {
                    const state = cell.getValue();
                    return <Chip label={state} color={getMachineStateColor(state)} size="small"/>;
                },
            },
            {
                accessorKey: 'disabled',
                header: 'Disabled',
                size: 50,
                Cell: ({cell}) => (cell.getValue() ? 'Yes' : 'No'),
            },
            {
                accessorKey: 'createdAt',
                header: 'Created',
                Cell: ({cell}) => new Date(cell.getValue()).toLocaleString()
            },
            {
                accessorKey: 'updatedAt',
                header: 'Updated',
                Cell: ({cell}) => new Date(cell.getValue()).toLocaleString()
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        manualSorting: true,
        rowCount,
        // The state and handlers now point to the variables from our hook
        state: {
            pagination,
            sorting,
            isLoading,
            showAlertBanner: !!error,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        muiToolbarAlertBannerProps: error
            ? {
                color: 'error',
                children: `Error: ${error}`,
            }
            : undefined,
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>
                Machines
            </Typography>
            <MaterialReactTable table={table}/>
        </Box>
    );
};

export default MachinesPage;